/**
 * CampaignWizard — 3-Schritt-Wizard zum Anlegen einer Kampagne.
 * Schritt 1: Stammdaten (Titel, Beschreibung, Verantwortlicher, Zielsegmente)
 * Schritt 2: Kunden zuordnen (FilterBuilder + akkumulierter Korb)
 * Schritt 3: Bestätigung + Speichern
 */
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Modal,
  Paper,
  Select,
  Stack,
  Stepper,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { hasRole, useCurrentUser } from "../../hooks/useAuth.js";
import { useEmployees } from "../../hooks/useEmployees.js";
import { useSegments } from "../../hooks/useSegments.js";
import { api } from "../../lib/api.js";
import {
  useCreateCampaign,
  type CreateCampaignPayload,
} from "../../hooks/useCampaigns.js";
import {
  FilterBuilder,
  emptyFilterState,
  type FilterState,
} from "./FilterBuilder.js";

// ── DTOs / lokale Typen ──────────────────────────────────────────────────────

interface SegmentOption {
  id: string;
  name: string;
}

interface EmployeeOption {
  id: string;
  fullName: string;
}

interface BasketItem {
  customerId: string;
  customerName: string;
  addType: "filter" | "manual";
}

// ── Schritt 1: Stammdaten ────────────────────────────────────────────────────

interface Step1Props {
  title: string;
  onTitleChange: (v: string) => void;
  description: string;
  onDescriptionChange: (v: string) => void;
  responsibleId: string;
  onResponsibleChange: (v: string) => void;
  targetSegmentIds: string[];
  onSegmentsChange: (ids: string[]) => void;
  employees: EmployeeOption[];
  segments: SegmentOption[];
}

function Step1Stammdaten({
  title,
  onTitleChange,
  description,
  onDescriptionChange,
  responsibleId,
  onResponsibleChange,
  targetSegmentIds,
  onSegmentsChange,
  employees,
  segments,
}: Step1Props) {
  const { t } = useTranslation();
  function toggleSegment(id: string) {
    if (targetSegmentIds.includes(id)) {
      onSegmentsChange(targetSegmentIds.filter((s) => s !== id));
    } else {
      onSegmentsChange([...targetSegmentIds, id]);
    }
  }

  return (
    <Stack gap="md">
      <TextInput
        label={t("pages.campaigns.fieldTitle")}
        placeholder={t("pages.campaigns.titlePlaceholder")}
        value={title}
        onChange={(e) => onTitleChange(e.currentTarget.value)}
        required
        withAsterisk
      />

      <Textarea
        label={t("pages.campaigns.fieldDescription")}
        placeholder={t("pages.campaigns.descriptionPlaceholder")}
        value={description}
        onChange={(e) => onDescriptionChange(e.currentTarget.value)}
        minRows={3}
        autosize
      />

      <Select
        label={t("pages.campaigns.fieldResponsible")}
        placeholder={t("pages.campaigns.responsiblePlaceholder")}
        data={employees.map((e) => ({ value: e.id, label: e.fullName }))}
        value={responsibleId || null}
        onChange={(v) => onResponsibleChange(v ?? "")}
        searchable
        clearable
      />

      <Box>
        <Text size="sm" fw={500} mb="xs">
          {t("pages.campaigns.fieldTargetSegments")} <Text span c="red">*</Text>
        </Text>
        <Text size="xs" c="dimmed" mb="xs">
          {t("pages.campaigns.targetSegmentsHint")}
        </Text>
        <Stack gap="xs">
          {segments.map((s) => (
            <Checkbox
              key={s.id}
              label={s.name}
              checked={targetSegmentIds.includes(s.id)}
              onChange={() => toggleSegment(s.id)}
            />
          ))}
          {segments.length === 0 && (
            <Text size="sm" c="dimmed">
              {t("pages.campaigns.noSegmentsAvailable")}
            </Text>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}

// ── Schritt 2: Kunden zuordnen ───────────────────────────────────────────────

interface Step2Props {
  filterState: FilterState;
  onFilterChange: (state: FilterState) => void;
  basket: BasketItem[];
  onAddToBasket: (items: BasketItem[]) => void;
  onRemoveFromBasket: (customerId: string) => void;
  availableComponents: Array<{ id: string; name: string }>;
  availableSegments: SegmentOption[];
}

function Step2KundenZuordnen({
  filterState,
  onFilterChange,
  basket,
  onAddToBasket,
  onRemoveFromBasket,
  availableComponents,
  availableSegments,
}: Step2Props) {
  const { t } = useTranslation();
  const basketIds = new Set(basket.map((b) => b.customerId));

  function handleAddCustomers(
    customers: Array<{ id: string; name: string }>,
    addType: "filter" | "manual",
  ) {
    const newItems: BasketItem[] = customers
      .filter((c) => !basketIds.has(c.id))
      .map((c) => ({
        customerId: c.id,
        customerName: c.name,
        addType,
      }));
    if (newItems.length > 0) onAddToBasket(newItems);
  }

  return (
    <Stack gap="md">
      <FilterBuilder
        value={filterState}
        onChange={onFilterChange}
        onAddCustomers={handleAddCustomers}
        basketCustomerIds={basketIds}
        availableComponents={availableComponents}
        availableSegments={availableSegments}
        showPreview
      />

      {basket.length > 0 && (
        <Box>
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={600}>
              {t("pages.campaigns.selectedCustomers")}
            </Text>
            <Badge color="blue" size="lg">
              {t("pages.campaigns.selectedCustomersCount", { count: basket.length })}
            </Badge>
          </Group>
          <Stack gap="xs">
            {basket.map((item) => (
              <Paper key={item.customerId} p="xs" withBorder>
                <Group justify="space-between">
                  <Group gap="xs">
                    <Text size="sm">{item.customerName || item.customerId}</Text>
                    <Badge size="xs" color={item.addType === "filter" ? "blue" : "gray"} variant="light">
                      {item.addType === "filter"
                        ? t("pages.campaigns.detail.viaFilterLabel")
                        : t("pages.campaigns.detail.viaManualLabel")}
                    </Badge>
                  </Group>
                  <Button
                    size="xs"
                    color="red"
                    variant="subtle"
                    onClick={() => onRemoveFromBasket(item.customerId)}
                  >
                    {t("common.remove")}
                  </Button>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {basket.length === 0 && (
        <Text size="sm" c="dimmed" ta="center" py="sm">
          {t("pages.campaigns.basketEmptyHint")}
        </Text>
      )}
    </Stack>
  );
}

// ── Schritt 3: Bestätigung ───────────────────────────────────────────────────

interface Step3Props {
  title: string;
  description: string;
  targetSegmentIds: string[];
  basket: BasketItem[];
  segments: SegmentOption[];
  canSelfApprove: boolean;
  onSaveAsDraft: () => void;
  onActivate: () => void;
  isSaving: boolean;
  error: string | null;
}

function Step3Bestaetigung({
  title,
  description,
  targetSegmentIds,
  basket,
  segments,
  canSelfApprove,
  onSaveAsDraft,
  onActivate,
  isSaving,
  error,
}: Step3Props) {
  const { t } = useTranslation();
  const filterCount = basket.filter((b) => b.addType === "filter").length;
  const manualCount = basket.filter((b) => b.addType === "manual").length;

  const segmentNames = targetSegmentIds
    .map((id) => segments.find((s) => s.id === id)?.name ?? id)
    .join(", ");

  return (
    <Stack gap="md">
      <Title order={4}>{t("pages.campaigns.summaryTitle")}</Title>

      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Group>
            <Text size="sm" fw={600} w={160}>
              {t("pages.campaigns.summaryTitleLabel")}
            </Text>
            <Text size="sm">{title}</Text>
          </Group>
          {description && (
            <Group align="flex-start">
              <Text size="sm" fw={600} w={160}>
                {t("pages.campaigns.summaryDescriptionLabel")}
              </Text>
              <Text size="sm" style={{ flex: 1 }}>
                {description}
              </Text>
            </Group>
          )}
          <Group>
            <Text size="sm" fw={600} w={160}>
              {t("pages.campaigns.summaryTargetSegmentsLabel")}
            </Text>
            <Text size="sm">{segmentNames || t("common.dashed")}</Text>
          </Group>
          <Group>
            <Text size="sm" fw={600} w={160}>
              {t("pages.campaigns.summaryCustomersTotalLabel")}
            </Text>
            <Text size="sm">
              {t("pages.campaigns.summaryCustomersBreakdown", {
                total: basket.length,
                filter: filterCount,
                manual: manualCount,
              })}
            </Text>
          </Group>
        </Stack>
      </Paper>

      {basket.length > 0 && (
        <Box style={{ maxHeight: 320, overflowY: "auto" }}>
          <Table withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("pages.campaigns.summaryTableCustomer")}</Table.Th>
                <Table.Th>{t("pages.campaigns.summaryTableVia")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {basket.map((item) => (
                <Table.Tr key={item.customerId}>
                  <Table.Td>{item.customerName || item.customerId}</Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={item.addType === "filter" ? "blue" : "gray"} variant="light">
                      {item.addType === "filter"
                        ? t("pages.campaigns.detail.viaFilterLabel")
                        : t("pages.campaigns.detail.viaManualLabel")}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      )}

      {error && (
        <Alert color="red" title={t("common.error")}>
          {error}
        </Alert>
      )}

      <Group justify="flex-end" gap="sm">
        <Button
          variant="default"
          onClick={onSaveAsDraft}
          loading={isSaving}
        >
          {t("pages.campaigns.saveAsDraft")}
        </Button>
        {canSelfApprove && (
          <Button
            color="green"
            onClick={onActivate}
            loading={isSaving}
          >
            {t("pages.campaigns.activateSelfApprove")}
          </Button>
        )}
      </Group>
    </Stack>
  );
}

// ── CampaignWizard ───────────────────────────────────────────────────────────

interface CampaignWizardProps {
  opened: boolean;
  onClose: () => void;
  /** Verfügbare Mitarbeiter für den Verantwortlichen-Dropdown */
  employees?: EmployeeOption[];
  /** Verfügbare Segmente */
  segments?: SegmentOption[];
  /** Verfügbare Komponenten für FilterBuilder */
  availableComponents?: Array<{ id: string; name: string }>;
}

export function CampaignWizard({
  opened,
  onClose,
  employees: employeesProp,
  segments: segmentsProp,
  availableComponents = [],
}: CampaignWizardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const createCampaign = useCreateCampaign();

  // Wenn nicht über Props geliefert: direkt vom Backend holen.
  const { data: fetchedEmployees } = useEmployees();
  const { data: fetchedSegments } = useSegments();
  const employees = employeesProp ?? fetchedEmployees ?? [];
  const segments = segmentsProp ?? fetchedSegments ?? [];

  // Schritt 1: Stammdaten
  const [activeStep, setActiveStep] = useState(0);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [responsibleId, setResponsibleId] = useState(user?.employeeId ?? "");
  const [targetSegmentIds, setTargetSegmentIds] = useState<string[]>([]);

  // Schritt 2: Filter + Korb
  const [filterState, setFilterState] = useState<FilterState>(emptyFilterState());
  const [basket, setBasket] = useState<BasketItem[]>([]);

  // Fehler
  const [saveError, setSaveError] = useState<string | null>(null);

  const canSelfApprove = hasRole(user, ["bereichsleiter", "stellvertreter"]);

  // Validierung Schritt 1
  const step1Valid = title.trim().length > 0 && targetSegmentIds.length > 0;

  function handleAddToBasket(items: BasketItem[]) {
    setBasket((prev) => {
      const existingIds = new Set(prev.map((b) => b.customerId));
      return [...prev, ...items.filter((item) => !existingIds.has(item.customerId))];
    });
  }

  function handleRemoveFromBasket(customerId: string) {
    setBasket((prev) => prev.filter((b) => b.customerId !== customerId));
  }

  async function saveWithStatus(selfApprove: boolean) {
    setSaveError(null);
    if (!title.trim()) {
      setSaveError(t("pages.campaigns.errorTitleRequired"));
      return;
    }
    if (targetSegmentIds.length === 0) {
      setSaveError(t("pages.campaigns.errorSegmentRequired"));
      return;
    }

    const payload: CreateCampaignPayload = {
      title: title.trim(),
      description: description.trim() || undefined,
      responsibleId: responsibleId || (user?.employeeId ?? ""),
      targetSegmentIds,
    };

    try {
      const campaign = await createCampaign.mutateAsync(payload);

      // 1. Mitglieder hinzufügen (falls Korb gefüllt)
      if (basket.length > 0) {
        await api.post(`/campaigns/${campaign.id}/participations/add`, {
          customers: basket.map((b) => ({
            customerId: b.customerId,
            addType: b.addType,
          })),
        });
      }

      // 2. Selbstfreigabe (draft → active) wenn aktiviert und Rolle passt
      if (selfApprove) {
        await api.post(`/campaigns/${campaign.id}/transition`, {
          to: "active",
        });
      }

      void navigate({ to: "/campaigns/$id", params: { id: campaign.id } });
      handleClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : t("pages.campaigns.errorSavingGeneric"));
    }
  }

  function handleClose() {
    setActiveStep(0);
    setTitle("");
    setDescription("");
    setResponsibleId(user?.employeeId ?? "");
    setTargetSegmentIds([]);
    setFilterState(emptyFilterState());
    setBasket([]);
    setSaveError(null);
    onClose();
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t("pages.campaigns.wizardTitle")}
      size="xl"
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <Stepper active={activeStep} onStepClick={setActiveStep} size="sm">
          <Stepper.Step
            label={t("pages.campaigns.wizardStep1Label")}
            description={t("pages.campaigns.wizardStep1Description")}
          >
            <Box pt="md">
              <Step1Stammdaten
                title={title}
                onTitleChange={setTitle}
                description={description}
                onDescriptionChange={setDescription}
                responsibleId={responsibleId}
                onResponsibleChange={setResponsibleId}
                targetSegmentIds={targetSegmentIds}
                onSegmentsChange={setTargetSegmentIds}
                employees={employees}
                segments={segments}
              />
            </Box>
          </Stepper.Step>

          <Stepper.Step
            label={t("pages.campaigns.wizardStep2Label")}
            description={t("pages.campaigns.wizardStep2Description")}
          >
            <Box pt="md">
              <Step2KundenZuordnen
                filterState={filterState}
                onFilterChange={setFilterState}
                basket={basket}
                onAddToBasket={handleAddToBasket}
                onRemoveFromBasket={handleRemoveFromBasket}
                availableComponents={availableComponents}
                availableSegments={segments}
              />
            </Box>
          </Stepper.Step>

          <Stepper.Step
            label={t("pages.campaigns.wizardStep3Label")}
            description={t("pages.campaigns.wizardStep3Description")}
          >
            <Box pt="md">
              <Step3Bestaetigung
                title={title}
                description={description}
                targetSegmentIds={targetSegmentIds}
                basket={basket}
                segments={segments}
                canSelfApprove={canSelfApprove}
                onSaveAsDraft={() => void saveWithStatus(false)}
                onActivate={() => void saveWithStatus(true)}
                isSaving={createCampaign.isPending}
                error={saveError}
              />
            </Box>
          </Stepper.Step>
        </Stepper>

        {/* Navigation */}
        {activeStep < 2 && (
          <Group justify="space-between" mt="md">
            <Button
              variant="default"
              onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
              disabled={activeStep === 0}
            >
              {t("common.back")}
            </Button>
            <Button
              onClick={() => setActiveStep((s) => s + 1)}
              disabled={activeStep === 0 && !step1Valid}
            >
              {t("common.next")}
            </Button>
          </Group>
        )}

        {activeStep > 0 && activeStep < 2 && (
          <Button
            variant="subtle"
            size="xs"
            onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
          >
            {t("common.previousPage")}
          </Button>
        )}
      </Stack>
    </Modal>
  );
}
